import { Button, Card, Text, Thumbnail, Tooltip } from '@shopify/polaris';
import { ImageIcon, XIcon } from '@shopify/polaris-icons';
import { useEffect, useRef, useState } from 'react';

export interface SelectedResource {
  id: string;
  handle: string;
  title?: string;
  image?: string;
}

interface SelectedResourceListProps {
  items: SelectedResource[];
  onRemove: (id: string) => void;
  resourceType: 'product' | 'collection';
  disabled?: boolean;
}

interface ResourceRowProps {
  item: SelectedResource;
  onRemove: (id: string) => void;
  resourceType: 'product' | 'collection';
  disabled?: boolean;
}

const ResourceRow = ({ item, onRemove, resourceType, disabled }: ResourceRowProps) => {
  const label = item.title || item.handle;
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const measure = () => {
      const el = textRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [label]);

  const title = (
    <div ref={textRef} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <Text as="span">{label}</Text>
    </div>
  );

  return (
    <Card padding="300">
      <div className="d-flex gap-8 align-center">
        {item.image ? (
          // Fixed 60x60 to match the placeholder; object-fit: contain shows the whole image (Polaris Thumbnail would crop it).
          <img
            src={item.image}
            alt={label}
            style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 8, flexShrink: 0, display: 'block' }}
          />
        ) : (
          <Thumbnail source={ImageIcon} alt={label} size="medium" />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>{isTruncated ? <Tooltip content={label}>{title}</Tooltip> : title}</div>
        <Button
          icon={XIcon}
          variant="tertiary"
          onClick={() => onRemove(item.id)}
          disabled={disabled}
          accessibilityLabel={`Remove ${resourceType} ${label}`}
        />
      </div>
    </Card>
  );
};

const SelectedResourceList = ({ items, onRemove, resourceType, disabled }: SelectedResourceListProps) => {
  if (items.length === 0) return null;

  return (
    <div className="mt-8" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item) => (
        <ResourceRow key={item.id} item={item} onRemove={onRemove} resourceType={resourceType} disabled={disabled} />
      ))}
    </div>
  );
};

export default SelectedResourceList;
